/**
 * Latency instrumentation for the debug overlay (?debug=1).
 *
 * Measures the JS stretch: pointerdown → playNote return. The real perceived
 * latency (finger → sound from the speaker) is only measured with hardware
 * (slow-motion video); this bounds the part we control and allows
 * comparing backends under equal conditions.
 */

export interface LatencySample {
  /** Milliseconds between the pointerdown and the trigger return. */
  dispatchMs: number;
  at: number;
}

const MAX_SAMPLES = 50;

export class LatencyMeter {
  private samples: LatencySample[] = [];

  /** Returns a callback that closes the measurement started in the gesture. */
  begin(): () => void {
    const start = performance.now();
    return () => {
      const dispatchMs = performance.now() - start;
      this.samples.push({ dispatchMs, at: Date.now() });
      if (this.samples.length > MAX_SAMPLES) {
        this.samples.shift();
      }
    };
  }

  last(): LatencySample | undefined {
    return this.samples.at(-1);
  }

  /** Average of the last samples (full buffer window). */
  averageMs(): number {
    if (this.samples.length === 0) {
      return 0;
    }
    const total = this.samples.reduce((acc, s) => acc + s.dispatchMs, 0);
    return total / this.samples.length;
  }

  /**
   * Percentile `p` (0..100) of `dispatchMs` over the window, by nearest-rank.
   * The mean hides tail jitter: in a kids' instrument what is perceived as
   * "laggy" is the p95/max latency of an isolated tap, not the average.
   * This is the number of the audio go/no-go.
   */
  percentileMs(p: number): number {
    if (this.samples.length === 0) {
      return 0;
    }
    const sorted = this.samples.map((s) => s.dispatchMs).toSorted((a, b) => a - b);
    const clamped = Math.min(100, Math.max(0, p));
    // 1-indexed nearest-rank: ceil(p/100 · N), clamped to [1, N].
    const rank = Math.min(sorted.length, Math.max(1, Math.ceil((clamped / 100) * sorted.length)));
    // `rank` ∈ [1, length] with length > 0 guaranteed above → always defined.
    return sorted[rank - 1]!;
  }

  /** Worst case of the window: the slowest trigger observed. */
  maxMs(): number {
    if (this.samples.length === 0) {
      return 0;
    }
    return this.samples.reduce((worst, s) => (s.dispatchMs > worst ? s.dispatchMs : worst), 0);
  }

  count(): number {
    return this.samples.length;
  }

  reset(): void {
    this.samples = [];
  }
}
